import styled from "../_snowpack/pkg/@emotion/styled.js";
import React, {useEffect, useRef, useState} from "../_snowpack/pkg/react.js";
import * as THREE from "../_snowpack/pkg/three.js";
import C from "../_snowpack/pkg/cannon.js";
import SimplexNoise from "../_snowpack/pkg/simplex-noise.js";
import gsap from "../_snowpack/pkg/gsap.js";
import {OrbitControls} from "../common/orbitcontrols.js";
import {media} from "./theme.js";
import {Routes} from "../typings/routes.js";
import {getUrl} from "../common/universal.js";
import Icon from "./icon.js";
let scene;
export default function ThreeEditor(props) {
  const {editorConfig, outsideTexturePath, productSlug, readOnly, ...rest} = props;
  const {standard, width = 0, length = 0, baseOutsideTexture} = editorConfig;
  const ref = useRef(null);
  const [windy, setWindy] = useState(readOnly ? false : true);
  if (standard !== "Seidenpapier") {
    return null;
  }
  const standardTextureUrl = getUrl(Routes.editorGetTexture, {}, {
    query: {
      width,
      slug: productSlug,
      height: length,
      base: baseOutsideTexture
    }
  });
  useEffect(() => {
    if (!ref.current)
      return;
    scene = new Scene(ref.current, length, width, standardTextureUrl);
    scene.setWindy(windy);
  }, [ref]);
  useEffect(() => {
    if (!scene)
      return;
    const url = getUrl(Routes.editorGetTexture, {}, {
      query: {
        width,
        slug: productSlug,
        path: outsideTexturePath,
        height: length,
        base: baseOutsideTexture
      }
    });
    scene.setTexture(url);
  }, [scene, outsideTexturePath]);
  function onClick() {
    const w = !windy;
    setWindy(w);
    scene.setWindy(w);
  }
  return /* @__PURE__ */ React.createElement(Container, {
    readOnly,
    ...rest
  }, /* @__PURE__ */ React.createElement(Canvas, {
    ref
  }), !readOnly && /* @__PURE__ */ React.createElement(PlayButton, {
    onClick
  }, windy ? /* @__PURE__ */ React.createElement(Icon, {
    name: "pause"
  }) : /* @__PURE__ */ React.createElement(Icon, {
    name: "play"
  })));
}
class Figure {
  constructor(scene2) {
    this.size = 8;
    this.mass = 1;
    this.sizes = new THREE.Vector2(0, 0);
    this.bufferV = new THREE.Vector3();
    this.bufferV2 = new C.Vec3();
    this.stitches = [];
    this.standardTextureUrl = scene2.standardTextureUrl;
    this.scene = scene2.scene;
    this.world = scene2.world;
    this.sizes.set(scene2.textureWidth, scene2.textureHeight);
    this.geometry = new THREE.PlaneBufferGeometry(1, 1, this.size, this.size);
    this.frontMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.75,
      side: THREE.DoubleSide
    });
    this.frontMesh = new THREE.Mesh(this.geometry, this.frontMaterial);
    this.frontMesh.position.set(0, 0, 0);
    this.frontMesh.scale.set(this.sizes.x, this.sizes.y, 1);
    this.scene.add(this.frontMesh);
    this.backMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.75,
      side: THREE.BackSide
    });
    this.backMesh = new THREE.Mesh(this.geometry, this.backMaterial);
    this.backMesh.position.set(0, 0, 0);
    this.backMesh.scale.set(this.sizes.x, this.sizes.y, 1);
    this.scene.add(this.backMesh);
    this.setBothMaterialImage(this.standardTextureUrl);
    this.createStitches();
  }
  setBothMaterialImage(src) {
    const loader = new THREE.TextureLoader();
    const texture = loader.load(src);
    this.frontMaterial.map = texture;
    this.frontMaterial.needsUpdate = true;
    this.backMaterial.map = texture;
    this.backMaterial.needsUpdate = true;
  }
  setFrontMaterialImage(src) {
    const loader = new THREE.TextureLoader();
    const texture = loader.load(src);
    this.frontMaterial.map = texture;
    this.frontMaterial.needsUpdate = true;
  }
  createStitches() {
    const {size, sizes, mass, geometry, stitches, world} = this;
    const particleShape = new C.Particle();
    const {position} = geometry.attributes;
    const {x: width, y: height} = sizes;
    for (let i = 0; i < position.count; i++) {
      const row = Math.floor(i / (size + 1));
      const pos = new C.Vec3(position.getX(i) * width, position.getY(i) * height, position.getZ(i));
      const stitch = new C.Body({
        mass: row === 0 ? 0 : mass / position.count,
        linearDamping: 0.8,
        position: pos,
        shape: particleShape
      });
      stitches.push(stitch);
      world.addBody(stitch);
    }
    for (let i = 0; i < position.count; i++) {
      const col = i % (size + 1);
      const row = Math.floor(i / (size + 1));
      if (col < size)
        this.connect(i, i + 1);
      if (row < size)
        this.connect(i, i + size + 1);
    }
  }
  connect(i, j) {
    const {stitches} = this;
    const c = new C.DistanceConstraint(stitches[i], stitches[j]);
    this.world.addConstraint(c);
  }
  applyWind(wind) {
    const {position} = this.geometry.attributes;
    for (let i = 0; i < position.count; i++) {
      const stitch = this.stitches[i];
      const windNoise = wind.flowfield[i];
      const tempPosPhysic = this.bufferV2.set(windNoise.x, windNoise.y, windNoise.z);
      stitch.applyForce(tempPosPhysic, C.Vec3.ZERO);
    }
  }
  update() {
    const {geometry, sizes, stitches, bufferV} = this;
    const {position} = geometry.attributes;
    const {x: width, y: height} = sizes;
    for (let i = 0; i < position.count; i++) {
      const p = bufferV.copy(stitches[i].position);
      position.setXYZ(i, p.x / width, p.y / height, p.z);
    }
    position.needsUpdate = true;
  }
}
class Wind {
  constructor(figure) {
    this.figure = figure;
    this.noise = new SimplexNoise();
    this.baseForce = 500;
    this.off = 0.05;
    this.clock = new THREE.Clock();
    this.direction = new THREE.Vector3(0.5, 0, -1);
    const {count} = figure.geometry.attributes.position;
    this.force = this.baseForce / count;
    this.flowfield = new Array(count);
    this.update();
  }
  update() {
    const {clock, figure, noise, off, flowfield, force, direction} = this;
    const time = clock.getElapsedTime();
    const {position} = figure.geometry.attributes;
    const size = figure.geometry.parameters.widthSegments;
    for (let i = 0; i < position.count; i++) {
      const col = i % (size + 1);
      const row = Math.floor(i / (size + 1));
      const f = (noise.noise3D(row * off, col * off, time) * 0.5 + 0.5) * force;
      flowfield[i] = direction.clone().multiplyScalar(f);
    }
  }
}
class Scene {
  constructor(canvas, textureWidth, textureHeight, standardTextureUrl) {
    this.canvas = canvas;
    this.textureWidth = textureWidth;
    this.textureHeight = textureHeight;
    this.standardTextureUrl = standardTextureUrl;
    this.windy = false;
    this.mouseDown = false;
    this.perspective = 800;
    this.setCanvasSize = () => {
      const {parentElement} = this.canvas;
      if (!parentElement)
        return;
      const {width, height} = parentElement.getBoundingClientRect();
      this.renderer.setSize(width, height);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    };
    this.onMouseMove = (evt) => {
      this.setWindDirection(evt);
    };
    this.setWindy = (w) => {
      this.windy = w;
    };
    this.update = () => {
      requestAnimationFrame(this.update);
      this.figure.update();
      if (this.windy) {
        this.wind.update();
        this.figure.applyWind(this.wind);
      }
      this.world.step(1 / 60);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    this.scene = new THREE.Scene();
    this.world = new C.World();
    this.world.gravity.set(0, -1e3, 0);
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true
    });
    const ambientlight = new THREE.AmbientLight(16777215, 2);
    this.scene.add(ambientlight);
    const fov = 180 * (2 * Math.atan(textureHeight / this.perspective)) / Math.PI;
    this.camera = new THREE.PerspectiveCamera(fov, 1, 1, 5e3);
    this.camera.position.set(0, 0, this.perspective);
    this.figure = new Figure(this);
    this.wind = new Wind(this.figure);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.setCanvasSize();
    this.bindEvents();
    this.update();
  }
  setTexture(src) {
    this.figure.setFrontMaterialImage(src);
  }
  bindEvents() {
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("resize", this.setCanvasSize);
  }
  setWindDirection({clientX: x, clientY: y}) {
    if (!this.windy)
      return;
    const {innerWidth: W, innerHeight: H} = window;
    gsap.to(this.wind.direction, {
      duration: 0.8,
      x: x / W - 0.5,
      y: -(y / H) + 0.5
    });
  }
}
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 40px;
  overflow: hidden;

  ${({backgroundColor}) => backgroundColor && `background: ${backgroundColor}70;`}

  ${({readOnly}) => readOnly && `
    pointer-events: none;
    padding: 0;
    height: auto;
  `}
`;
const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;
const PlayButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 40px;
  width: 40px;
  height: 40px;
  border-radius: 100%;
  background: white;
  cursor: pointer;

  ${media.mobile} {
    bottom: auto;
    left: 20px;
    top: 70px;
    transform: none;
  }
`;
